<script setup lang="ts">
/**
 * LLM-usage rollup panel — query /api/admin/usage with a group_by selector
 * + optional time bounds. Shows totals and per-bucket numbers.
 */
import { computed, onMounted, ref } from "vue";
import { AdminError, getUsage, type UsageRow } from "../api/admin";
import { useToast } from "../composables/useToast";
import SkeletonRows from "./SkeletonRows.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();

// `tenant` is deliberately absent. The backend still aggregates by it —
// multi-tenant deploys will want it — but this is a single-tenant install,
// so the option only ever produced one row called "doit".
type GroupBy = "user_oid" | "assistant_slug" | "model";

const groupBy = ref<GroupBy>("user_oid");
const fromTs = ref("");
const toTs = ref("");
const rows = ref<UsageRow[]>([]);
const loading = ref(false);
/** Display currency for the cost columns — comes from the budget policy,
 * so the Usage page and the spend limits always speak the same units. */
const currency = ref("DKK");
/** Service credentials (`apikey:<id>`) are real spend, but they are not
 * people, and on the User grouping they crowd out the question the page is
 * usually answering ("who is using this?"). Hidden by default, one click
 * away — never dropped from the query, so turning them on costs no refetch. */
const showApiKeys = ref(false);
/** Internal-tester principals (USAGE_INTERNAL_OIDS) are requested by
 * default here: their spend is still spend. The toggle hides them for the
 * "how is the product being used" question. */
const showInternal = ref(true);

/** Models seen in the window that the backend rate card cannot price. Their
 * turns cost 0.00 here and count against nobody's spend budget, so the page
 * has to say so — a silent 0 reads as "free", not as "unmeasured". */
const unpricedModels = ref<string[]>([]);

async function reload() {
  loading.value = true;
  try {
    const result = await getUsage(
      groupBy.value,
      props.devUser,
      fromTs.value || undefined,
      toTs.value || undefined,
      showInternal.value,
    );
    rows.value = result.rows;
    currency.value = result.currency ?? "DKK";
    unpricedModels.value = result.unpriced_models ?? [];
  } catch (e) {
    toast.error(
      "Could not load usage",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

const isApiKey = (r: UsageRow): boolean =>
  String(r.user_oid ?? "").startsWith("apikey:");

/** What the whole page renders — chart, table, stat cards and totals all
 * read this, so a hidden principal is hidden from the sums too. A total
 * that silently included rows you cannot see would be worse than either
 * showing or hiding them consistently. */
const shown = computed(() =>
  groupBy.value === "user_oid" && !showApiKeys.value
    ? rows.value.filter((r) => !isApiKey(r))
    : rows.value,
);

const hiddenApiKeys = computed(() =>
  groupBy.value === "user_oid" ? rows.value.filter(isApiKey).length : 0,
);

const totals = computed(() => ({
  turns: shown.value.reduce((s, r) => s + (r.turns ?? 0), 0),
  prompt_tokens: shown.value.reduce(
    (s, r) => s + ((r.prompt_tokens as number) ?? 0),
    0,
  ),
  completion_tokens: shown.value.reduce(
    (s, r) => s + ((r.completion_tokens as number) ?? 0),
    0,
  ),
  latency_ms_total: shown.value.reduce(
    (s, r) => s + ((r.latency_ms_total as number) ?? 0),
    0,
  ),
}));

function avgLatency(r: UsageRow): string {
  const t = r.turns ?? 0;
  const total = (r.latency_ms_total as number) ?? 0;
  if (t === 0) return "—";
  return `${Math.round(total / t)} ms`;
}

/** The raw grouped value — an oid, a slug, a model name. Always the stable
 * identity, never the display label; used for keys and `title=` tooltips. */
function bucketId(r: UsageRow): string {
  const v = r[groupBy.value];
  if (v === null || v === undefined || v === "") return "(empty)";
  return String(v);
}

/** What a human reads. For the user grouping that is their email (or
 * display name), falling back to the raw principal id — `apikey:<n>`
 * credentials and GDPR-deleted users have no `users` row, and an opaque
 * GUID is still better than dropping the row. The id stays reachable as
 * the element's tooltip either way. */
function bucketLabel(r: UsageRow): string {
  if (groupBy.value === "user_oid") {
    const email = (r.email as string | null) ?? "";
    if (email) return email;
    const name = (r.display_name as string | null) ?? "";
    if (name) return name;
  }
  return bucketId(r);
}

/** Tooltip: the identity behind the label, plus the label when they differ. */
function bucketTitle(r: UsageRow): string {
  const id = bucketId(r);
  const label = bucketLabel(r);
  if (label === id) return id;
  const name = (r.display_name as string | null) ?? "";
  return name && name !== label ? `${name} · ${id}` : id;
}

/** Display-currency cost, computed server-side from the backend rate card.
 * This page used to hold its own copy of the rate table, which meant the
 * numbers drifted from the ones the spend cap enforces and only existed for
 * group_by=model. There is now one rate card, in the backend. */
function cost(r: UsageRow): number {
  return (r.cost as number) ?? 0;
}

/** Rows whose cost is understated because some of their tokens went to a
 * model with no rate. */
function unpricedIn(r: UsageRow): string[] {
  return (r.unpriced_models as string[]) ?? [];
}

const formatNum = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatMoney = (n: number): string =>
  `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency.value}`;

// Bar-chart values — sort rows by combined tokens descending so
// the chart reads left-to-right "most expensive first".
const chartRows = computed(() => {
  const enriched = shown.value.map((r) => {
    const prompt = (r.prompt_tokens as number) ?? 0;
    const completion = (r.completion_tokens as number) ?? 0;
    return {
      key: bucketId(r),
      label: bucketLabel(r),
      title: bucketTitle(r),
      turns: r.turns ?? 0,
      total: prompt + completion,
      prompt,
      completion,
      cost: cost(r),
    };
  });
  enriched.sort((a, b) => b.total - a.total);
  return enriched.slice(0, 20); // top-20 keeps the chart legible
});

const groupLabel = computed(
  () =>
    ({ user_oid: "User", assistant_slug: "Assistant", model: "Model" })[
      groupBy.value
    ],
);

const maxBucketTotal = computed(() =>
  Math.max(1, ...chartRows.value.map((r) => r.total)),
);

// Stat cards. Cost is now summable for every grouping, so the card is
// unconditional — it was previously hidden on any grouping but `model`.
const totalsExtra = computed(() => {
  return {
    cost: shown.value.reduce((s, r) => s + cost(r), 0),
    totalTokens: totals.value.prompt_tokens + totals.value.completion_tokens,
    avgLatencyMs:
      totals.value.turns > 0
        ? Math.round(totals.value.latency_ms_total / totals.value.turns)
        : 0,
  };
});
</script>

<template>
  <div class="admin-usage">
    <header class="admin-usage__header">
      <h2>LLM usage</h2>
      <div class="filters">
        <label>
          Group by
          <select v-model="groupBy" @change="reload">
            <option value="user_oid">User</option>
            <option value="assistant_slug">Assistant</option>
            <option value="model">Model</option>
          </select>
        </label>
        <label>
          From
          <input type="datetime-local" v-model="fromTs" @change="reload" />
        </label>
        <label>
          To
          <input type="datetime-local" v-model="toTs" @change="reload" />
        </label>
        <label v-if="groupBy === 'user_oid'" class="check">
          <input type="checkbox" v-model="showApiKeys" />
          API keys
        </label>
        <label class="check">
          <input type="checkbox" v-model="showInternal" @change="reload" />
          Internal testers
        </label>
        <button @click="reload">Refresh</button>
      </div>
    </header>

    <!-- An unpriced model is billed at 0.00 by pricing.py, which never
         invents a rate. Unannounced that looks like free usage rather than
         unmeasured usage, and those turns also escape every spend budget —
         so say it once, loudly, above the numbers it affects. -->
    <p v-if="!loading && unpricedModels.length > 0" class="warn-banner">
      <strong>Costs below are an under-estimate.</strong>
      No rate-card entry for
      <code v-for="m in unpricedModels" :key="m">{{ m }}</code
      >, so those turns are counted at 0 here and against nobody&#39;s spend
      budget. Add them to <code>LLM_RATE_CARD_JSON</code> to price them.
    </p>

    <!-- Stat cards — at-a-glance totals over the selected window. Cost is
         priced server-side per (bucket, model) and summed, so it is
         meaningful on every grouping, not just Model. -->
    <section v-if="!loading && shown.length > 0" class="stats">
      <div class="stat">
        <div class="stat__label">Turns</div>
        <div class="stat__value">{{ formatNum(totals.turns) }}</div>
      </div>
      <div class="stat">
        <div class="stat__label">Prompt tokens</div>
        <div class="stat__value">{{ formatNum(totals.prompt_tokens) }}</div>
      </div>
      <div class="stat">
        <div class="stat__label">Completion tokens</div>
        <div class="stat__value">{{ formatNum(totals.completion_tokens) }}</div>
      </div>
      <div class="stat">
        <div class="stat__label">Avg latency</div>
        <div class="stat__value">{{ totalsExtra.avgLatencyMs }} ms</div>
      </div>
      <div class="stat stat--accent">
        <div class="stat__label">Est. spend</div>
        <div class="stat__value">{{ formatMoney(totalsExtra.cost) }}</div>
        <div class="stat__hint">rate card × per-model tokens</div>
      </div>
    </section>

    <!-- Horizontal stacked bar chart per bucket — pure SVG, no chart
         lib. Top 20 buckets by total tokens; rest collapsed into
         the table below for detail. -->
    <section v-if="!loading && chartRows.length > 0" class="chart">
      <h3 class="chart__title">Top {{ chartRows.length }} by tokens</h3>
      <div class="chart__rows">
        <div v-for="r in chartRows" :key="r.key" class="chart-row">
          <div class="chart-row__label" :title="r.title">{{ r.label }}</div>
          <div class="chart-row__bar-wrap">
            <div
              class="chart-row__bar chart-row__bar--prompt"
              :style="{ width: `${(r.prompt / maxBucketTotal) * 100}%` }"
              :aria-label="`${r.key} prompt tokens: ${r.prompt}`"
            ></div>
            <div
              class="chart-row__bar chart-row__bar--completion"
              :style="{ width: `${(r.completion / maxBucketTotal) * 100}%` }"
              :aria-label="`${r.key} completion tokens: ${r.completion}`"
            ></div>
          </div>
          <div class="chart-row__total">
            {{ formatNum(r.total) }}
            <span class="chart-row__cost"> · {{ formatMoney(r.cost) }} </span>
          </div>
        </div>
      </div>
      <div class="chart__legend">
        <span class="legend-swatch legend-swatch--prompt"></span> prompt
        <span class="legend-swatch legend-swatch--completion"></span> completion
      </div>
    </section>

    <p
      v-if="!loading && hiddenApiKeys > 0 && !showApiKeys"
      class="muted hint-line"
    >
      {{ hiddenApiKeys }} API-key
      {{ hiddenApiKeys === 1 ? "principal" : "principals" }} hidden — tick
      &ldquo;API keys&rdquo; to include their spend.
    </p>

    <table v-if="!loading" class="admin-table">
      <thead>
        <tr>
          <th>{{ groupLabel }}</th>
          <th class="num">Turns</th>
          <th class="num">Prompt tokens</th>
          <th class="num">Completion tokens</th>
          <th class="num">Avg latency</th>
          <th class="num">Est. cost</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(r, i) in shown" :key="i">
          <td :title="bucketTitle(r)">{{ bucketLabel(r) }}</td>
          <td class="num">{{ formatNum(r.turns ?? 0) }}</td>
          <td class="num">{{ formatNum((r.prompt_tokens as number) ?? 0) }}</td>
          <td class="num">
            {{ formatNum((r.completion_tokens as number) ?? 0) }}
          </td>
          <td class="num">{{ avgLatency(r) }}</td>
          <td class="num">
            {{ formatMoney(cost(r)) }}
            <span
              v-if="unpricedIn(r).length > 0"
              class="unpriced"
              :title="`Under-estimate — no rate for ${unpricedIn(r).join(', ')}`"
              >*</span
            >
          </td>
        </tr>
        <tr v-if="shown.length === 0">
          <td colspan="6" class="muted">No usage rows in this window.</td>
        </tr>
      </tbody>
      <tfoot v-if="shown.length > 0">
        <tr>
          <th>Total</th>
          <th class="num">{{ formatNum(totals.turns) }}</th>
          <th class="num">{{ formatNum(totals.prompt_tokens) }}</th>
          <th class="num">{{ formatNum(totals.completion_tokens) }}</th>
          <th class="num">
            {{
              totals.turns > 0
                ? `${Math.round(totals.latency_ms_total / totals.turns)} ms`
                : "—"
            }}
          </th>
          <th class="num">{{ formatMoney(totalsExtra.cost) }}</th>
        </tr>
      </tfoot>
    </table>
    <SkeletonRows v-else :rows="6" />
  </div>
</template>

<style scoped>
.admin-usage {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-usage__header {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  justify-content: space-between;
  align-items: flex-end;
}
.check {
  flex-direction: row !important;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  padding-bottom: 0.35rem;
}
.hint-line {
  margin: 0;
  font-size: 0.82rem;
}
.admin-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.admin-table tfoot th {
  font-weight: 700;
  border-top: 2px solid var(--color-border);
}
.muted {
  color: var(--color-muted);
}
.warn-banner {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  border: 1px solid var(--color-warn);
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--color-warn) 8%, transparent);
  color: var(--color-ink);
}
.warn-banner code {
  margin: 0 0.2rem;
}
.unpriced {
  color: var(--color-warn);
  font-weight: 700;
  cursor: help;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}

/* ── Stat cards ───────────────────────────────────────────────── */
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-3);
}
.stat {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.stat--accent {
  border-color: var(--color-accent);
  background: var(--color-accent-dim);
}
.stat__label {
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-muted);
  font-weight: 600;
}
.stat__value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.stat__hint {
  font-size: 0.72rem;
  color: var(--color-muted);
}

/* ── Bar chart ─────────────────────────────────────────────────── */
.chart {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
}
.chart__title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  color: var(--color-ink);
}
.chart__rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.chart-row {
  display: grid;
  grid-template-columns: minmax(120px, 200px) 1fr minmax(120px, auto);
  gap: var(--space-3);
  align-items: center;
  font-size: 0.85rem;
  padding: 4px 0;
}
.chart-row__label {
  color: var(--color-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.chart-row__bar-wrap {
  display: flex;
  align-items: stretch;
  height: 16px;
  background: var(--color-surface-alt);
  border-radius: 4px;
  overflow: hidden;
}
.chart-row__bar {
  height: 100%;
  transition: width 240ms ease;
}
.chart-row__bar--prompt {
  background: var(--color-accent);
}
.chart-row__bar--completion {
  background: var(--color-accent-dim);
  border-left: 1px solid var(--color-accent);
}
.chart-row__total {
  font-variant-numeric: tabular-nums;
  color: var(--color-muted);
  text-align: right;
  font-size: 0.85rem;
}
.chart-row__cost {
  color: var(--color-accent);
  font-weight: 600;
}
.chart__legend {
  display: flex;
  gap: var(--space-3);
  font-size: 0.78rem;
  color: var(--color-muted);
  margin-top: 4px;
  align-items: center;
}
.legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin: 0 4px 0 12px;
  vertical-align: middle;
}
.legend-swatch--prompt {
  background: var(--color-accent);
}
.legend-swatch--completion {
  background: var(--color-accent-dim);
  border: 1px solid var(--color-accent);
}
</style>
